import ClipLoader from "react-spinners/ClipLoader";
import React, { memo, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Modal from "react-responsive-modal";
import { toast } from "react-toastify";
import adManagerbackend from "../../../../../api/api3";
import { global_actions } from "../../../../../redux-store";
import useSampleUpload from "./hooks/useSampleUpload";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { formatPrice } from "../../../../../utils/formatPrice";
import backend from "../../../../../api";

const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#32325d",
      "::placeholder": {
        color: "#aab7b8",
      },
    },
    invalid: {
      color: "#e54242",
    },
  },
};

const CreateAdModal = ({ open, close, getAds }) => {
  const {
    handleSubmit,
    register,
    setError,
    setValue,
    getValues,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm();
  const { isSampleUploading, uploadSample } = useSampleUpload();
  const { user } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.global);
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const { retailValue, ad_level, ad_price } = getValues();

  const [retailValues, setRetailValues] = useState([]);
  const [adLevels, setAdLevels] = useState([]);
  const [selectedRetailValue, setSelectedRetailValue] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [adFileUrl, setAdFileUrl] = useState("");
  const [step, setStep] = useState(0);
  const [paymentType, setPaymentType] = useState("card-number");
  const [calculation, setCalculation] = useState({
    grandTotal: 0,
    adPrice: 0,
    transactionFee: 0,
  });

  const [accounts, setAccounts] = useState([]);

  const fetchAdLevels = async () => {
    try {
      const { data } = await adManagerbackend.get("/adlevels", {
        params: { type: "eksplodeRetailers" },
      });
      setRetailValues(data);
    } catch (error) {
      console.log("error: ", error);
      toast.error("Failed to load retail values");
    }
  };

  const fetchEMoneyAccounts = async (paymentType) => {
    if (paymentType !== "e-money") {
      return;
    }

    try {
      const response = await backend.get("/accounts", {
        params: {
          userId: user?.user?._id,
          type: "e-money",
        },
      });

      const { accounts } = response?.data;
      setAccounts(accounts);
    } catch (error) {
      console.error("error: ", error);
    }
  };

  const calculatePricing = (ad_price, paymentType) => {
    let STRIPE_PERCENTAGE = 0;
    let STRIPE_FIXED = 0;
    if (paymentType === "card-number") {
      STRIPE_PERCENTAGE = 2.9 / 100;
      STRIPE_FIXED = 0.3;
    }
    const adPrice = parseFloat(ad_price);
    const grandTotal = (adPrice + STRIPE_FIXED) / (1 - STRIPE_PERCENTAGE);

    const transactionFee = grandTotal - adPrice;
    setCalculation({ grandTotal, adPrice, transactionFee });
  };

  const getVideoDuration = (file, threshold) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const media = new Audio(reader.result);
        media.onloadedmetadata = () => {
          const duration = parseInt(media.duration, 10);
          if (duration >= threshold[0] && duration <= threshold[1]) {
            resolve(true);
          } else {
            resolve(false);
          }
        };
      };
      reader.onerror = () => resolve(false);
      reader.readAsDataURL(file);
    });
  };

  const handleCardChange = (event) => {
    const { complete, empty, error } = event;
    if (complete) {
      clearErrors("methodId");
    }
  };

  const handleStepChange = async (value) => {
    const isValid = await trigger();
    if (isValid) {
      setStep(value);
    }
  };

  const handleType = (e) => {
    setPaymentType(e.target.value);
  };

  const handleRetailValueChange = (e) => {
    const retailValueId = e.target.value;
    const selectedValue = retailValues.find(
      (value) => value._id === retailValueId
    );
    setSelectedRetailValue(selectedValue);
    setSelectedLevel(null);
    setValue("retailValue", selectedValue?._id);
    setValue("ad_level", "");
    setValue("ad_price", "");
    setAdLevels(
      selectedValue?.adlevels?.find((ad) => ad.type === "eksplodeRetailers")
        ?.levels || []
    );
    clearErrors("ad_level");
  };

  const handleLevelChange = (e) => {
    const levelId = e.target.value;
    const selected = adLevels.find((level) => level._id === levelId);
    setSelectedLevel(selected);
    setValue("ad_level", selected?._id);
    setValue("ad_price", "");
    if (selected) clearErrors("ad_price");
  };

  const handleBidChange = (e) => {
    const bid = parseFloat(e.target.value);
    if (selectedLevel && bid < selectedLevel.price) {
      setError("ad_price", {
        type: "manual",
        message: `Bid must be at least ${selectedLevel.price}$`,
      });
    } else {
      setValue("ad_price", e.target.value);
      clearErrors("ad_price");
    }
  };

  const uploadAd = async (file) => {
    if (!file) {
      setError("ad_file", { type: "manual", message: "No File Found" });
      return;
    }
    const extension = file.type.split("/").slice(-1)[0];
    const isVideo = ["mp4", "mov", "mpeg4", "m4v", "avi", "m4p"].includes(
      extension.toLowerCase()
    );

    const duration = [25, 45]; // [minimum, maximum];

    if (isVideo) {
      const isValid = await getVideoDuration(file, duration);

      if (!isValid) {
        setError("ad_file", {
          type: "manual",
          message: `Video file length must be between ${duration[0]} to ${duration[1]}`,
        });
        return;
      }
    }

    const value = await uploadSample(file);

    if (!value) {
      setError("ad_file", { type: "manual", message: "File upload failed" });
      return;
    }

    setAdFileUrl(value);
    clearErrors("ad_file");
  };

  const submitModal = async (formData) => {
    dispatch(global_actions.loading(true));

    formData.ad_level = selectedLevel?.level;
    formData.ad_file = adFileUrl;
    formData.created_by = user?.user?._id;
    formData.retailValueOfPublication =
      selectedRetailValue?.retailValueOfPublication;
    formData.payment_type = paymentType;
    formData.calculation = calculation;

    if (paymentType === "card-number") {
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
        billing_details: {
          email: user?.user?.email,
        },
      });

      if (error) {
        console.error("Error in createPaymentMethod:", error);
        dispatch(global_actions.loading(false));
        setError("cardNumber", {
          type: "manual",
          message: error.message,
        });
        return;
      }

      formData.payment_id = paymentMethod?.id;
      clearErrors("cardNumber");
    }

    // console.log("formData: ", formData);
    // return;

    try {
      await adManagerbackend.post("/adworkshop", formData);
      toast.success("Ad created successfully!");
      getAds();
      close();
    } catch (error) {
      console.log("error in creating ad: ", error);
      toast.error("Failed to create ad");
    } finally {
      dispatch(global_actions.loading(false));
    }
  };

  useEffect(() => {
    calculatePricing(ad_price, paymentType);
  }, [paymentType, ad_price]);

  useEffect(() => {
    fetchEMoneyAccounts(paymentType);
  }, [paymentType]);

  useEffect(() => {
    fetchAdLevels();
  }, []);

  return (
    <Modal open={open} onClose={close} center>
      <div className="container px-5 pt-5">
        <form onSubmit={handleSubmit(submitModal)}>
          <div className={`${step === 0 ? "form-row" : "d-none"}`}>
            <div className="col-12 mb-3">
              <label htmlFor="ad_name">Ad Name</label>
              <input
                type="text"
                className="form-control"
                id="ad_name"
                name="ad_name"
                ref={register({ required: "This Field is Required" })}
              />
              {errors.ad_name && (
                <span className="text-danger">{errors.ad_name.message}</span>
              )}
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="redirect_url">Redirect URL</label>
              <input
                type="text"
                className="form-control"
                id="redirect_url"
                name="redirect_url"
                ref={register({
                  required: "This Field is Required",
                  pattern: {
                    value: new RegExp(
                      "^(https?://)((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|\\d{1,3}(\\.\\d{1,3}){3})(:\\d+)?(/[-a-z\\d%_.~+]*)*(\\?[;&a-z\\d%_.~+=-]*)?(#[-a-z\\d_]*)?$",
                      "i"
                    ),
                    message: "This must be a valid URL",
                  },
                })}
              />
              {errors.redirect_url && (
                <span className="text-danger">
                  {errors.redirect_url.message}
                </span>
              )}
            </div>
            <div className="col-12 mb-3">
              <label>Select Retail Value</label>
              <select
                className="form-control"
                name="retailValue"
                ref={register({ required: "This field is required" })}
                onChange={handleRetailValueChange}
              >
                <option value="">Select Retail Value</option>
                {retailValues.map((value) => (
                  <option key={value._id} value={value._id}>
                    {value.retailValueOfPublication.minimum || "Below"} -{" "}
                    {value.retailValueOfPublication.maximum || "Above"}
                  </option>
                ))}
              </select>
              {errors.retailValue && (
                <span className="text-danger">
                  {errors.retailValue.message}
                </span>
              )}
            </div>
            <div className="col-lg-6 mb-3">
              <label>Select Ad Level</label>
              <select
                className="form-control"
                name="ad_level"
                ref={register({ required: "This field is required" })}
                onChange={handleLevelChange}
                disabled={!selectedRetailValue}
              >
                <option value="">Select Ad Level</option>
                {adLevels.map((level) => (
                  <option key={level._id} value={level._id}>
                    Level {level.level} (Min Bid: ${level.price})
                  </option>
                ))}
              </select>
              {errors.ad_level && (
                <span className="text-danger">{errors.ad_level.message}</span>
              )}
            </div>
            <div className="col-lg-6 mb-3">
              <label>Your Bid</label>
              <input
                type="text"
                className="form-control"
                name="ad_price"
                disabled={!selectedLevel}
                ref={register({
                  required: "Bid value is required",
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message:
                      "Only valid numbers are allowed (e.g., 1, 1.5, 1.75)",
                  },
                })}
                onChange={handleBidChange}
              />
              {errors.ad_price && (
                <span className="text-danger">{errors.ad_price.message}</span>
              )}
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="ad_file">Select Ad File</label>
              <input
                type="file"
                className="form-control"
                accept="video/*,image/*"
                name="ad_file"
                ref={register({ required: "This Field is Required" })}
                onChange={(e) => uploadAd(e.target.files[0])}
              />
              {errors.ad_file && (
                <span className="text-danger">{errors.ad_file.message}</span>
              )}
            </div>

            <div className="col-12">
              <div className="d-flex justify-content-end">
                <button
                  className="btn btn-solid"
                  onClick={() => handleStepChange(1)}
                  type="button"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <div className={`${step === 1 ? "form-row" : "d-none"}`}>
            <div className="col-12 mb-3">
              <label htmlFor="fname">Selected Payment Type</label>
              <div className="d-flex align-items-center">
                <div className="d-flex justify-content-start mr-2">
                  <input
                    type="radio"
                    className="form-control w-auto mr-2"
                    id="card-number"
                    name="card-number"
                    value="card-number"
                    checked={paymentType === "card-number"}
                    onChange={handleType}
                  />
                  <label htmlFor="card-number" className="mb-0">
                    Card-Number
                  </label>
                </div>

                <div className="d-flex justify-content-start">
                  <input
                    type="radio"
                    className="form-control w-auto mr-2"
                    id="e-money"
                    name="e-money"
                    value="e-money"
                    checked={paymentType === "e-money"}
                    onChange={handleType}
                  />
                  <label htmlFor="e-money" className="mb-0">
                    E-Money
                  </label>
                </div>
              </div>
            </div>

            <div className="col-12 mb-3">
              {paymentType === "e-money" ? (
                <>
                  <label htmlFor="ad_file">E-money Account No.</label>
                  <select
                    className="form-control"
                    id="payment_id"
                    name="payment_id"
                    ref={register({ required: "This Field is Required" })}
                  >
                    <option selected disabled value="">
                      Select E-money Account
                    </option>
                    {accounts?.map((el, index) => {
                      return (
                        <option key={index} value={el?.accountId}>
                          {el?.accountId}
                        </option>
                      );
                    })}
                  </select>
                  {errors.payment_id && (
                    <span className="text-danger">
                      {errors.payment_id.message}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <label htmlFor="ad_file">Card Number</label>
                  <CardElement
                    id="cardDetails"
                    options={cardElementOptions}
                    onChange={handleCardChange}
                  />
                </>
              )}
            </div>

            <div className="col-12">
              <div className="order-box">
                <ul className="sub-total">
                  <li>
                    Ad Price
                    <span className="count text-right">
                      {formatPrice(calculation?.adPrice)}
                    </span>
                  </li>

                  <li>
                    Transaction Fee
                    <div className="shipping text-right">
                      <label htmlFor="deliveryCharges" className="mb-0">
                        {formatPrice(calculation?.transactionFee)}
                      </label>
                    </div>
                  </li>
                </ul>
                <ul className="total">
                  <li>
                    Grand Total
                    <span className="count text-right">
                      {formatPrice(calculation?.grandTotal)}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="col-12">
              <div className="d-flex justify-content-end">
                <button
                  className="btn btn-solid mr-2"
                  onClick={() => handleStepChange(0)}
                  type="button"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-solid"
                  disabled={isSampleUploading || loading}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>

          <ClipLoader
            color={"#000"}
            loading={isSampleUploading || loading}
            size={30}
          />
        </form>
      </div>
    </Modal>
  );
};

export default memo(CreateAdModal);
